import { useCallback } from "react";
import { AssetType } from "@zapier/canvas-state";

interface UseAssetNavigationOptions {
  url: string | undefined;
  assetType: AssetType;
  onAssetOpen?: (params: {
    asset: "zap" | "table" | "interface" | "chatbot" | "agent";
    location: "node" | "sidebar" | "tour" | "focus mode";
  }) => void;
}

interface UseAssetNavigationReturn {
  handleGoToAsset: () => void;
  canNavigate: boolean;
}

/**
 * Custom hook for handling asset navigation and URL manipulation.
 * Manages URL construction, search params, and opening assets in new tabs.
 */
export function useAssetNavigation({
  url,
  assetType,
  onAssetOpen,
}: UseAssetNavigationOptions): UseAssetNavigationReturn {
  const handleGoToAsset = useCallback(() => {
    if (!url) return;

    try {
      // Handle agent-specific URL transformation
      const processedUrl =
        assetType === "agent"
          ? url.replace("/settings-preview", "/settings?tab=chat")
          : url;

      const editUrl = new URL(processedUrl, window.location.origin);

      // Emit analytics event
      if (assetType && onAssetOpen) {
        onAssetOpen({
          asset: assetType as
            | "zap"
            | "table"
            | "interface"
            | "chatbot"
            | "agent",
          location: "focus mode",
        });
      }

      // Open in new tab
      window.open(editUrl.toString(), "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error(`Failed to open ${assetType} URL:`, error);
    }
  }, [url, assetType, onAssetOpen]);

  return {
    handleGoToAsset,
    canNavigate: !!url,
  };
}
